import React, { useState, useRef, useEffect } from 'react';
import Sketch from 'react-p5';
import audioManager from '../../../utils/filemanager/AudioManager';

const titleBarStyle = {
  backgroundColor: '#000000',
  borderBottom: '2px solid #00ff00',
  padding: '12px 20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontFamily: 'IBM BIOS, monospace',
  color: '#00ff00',
  fontSize: '20px',
  textShadow: '0 0 8px #00ff00',
  width: '100%',
  boxSizing: 'border-box',
  letterSpacing: '1px'
};

const QubitHarvestingAnimation = ({ results, onComplete }) => {
  // Ensure safe values for animation parameters
  const safeQubits = results?.qubits ? parseInt(results.qubits) : 0;
  const particleCount = Math.min(Math.max(safeQubits || 75, 20), 150);
  const baseSize = 4;
  const pulseRange = 3;
  const trailLength = 12;
  
  // Validate results object
  const validResults = results && {
    qubits: safeQubits,
    bonuses: Array.isArray(results.bonuses) ? results.bonuses : [],
    totalMultiplier: parseFloat(results.totalMultiplier) || 1,
    totalHarvested: parseInt(results.totalHarvested) || 0
  };
  
  const [progress, setProgress] = useState(0);
  const [displayedQubits, setDisplayedQubits] = useState(0);
  const particles = useRef([]);
  const startTime = useRef(Date.now());
  const audioSource = useRef(null);
  const successPlayed = useRef(false);

  // Initialize and play qubits sound
  useEffect(() => {
    const initAudio = async () => {
      try {
        if (!audioManager.isInitialized()) {
          await audioManager.init();
        }
        
        // Create and play qubits sound
        if (audioManager.audioContext) {
          const response = await fetch(`${process.env.REACT_APP_AUDIO_BASE_URL}/qubits.mp3`);
          const arrayBuffer = await response.arrayBuffer();
          const audioBuffer = await audioManager.audioContext.decodeAudioData(arrayBuffer);
          
          const source = audioManager.audioContext.createBufferSource();
          const gainNode = audioManager.audioContext.createGain();
          
          source.buffer = audioBuffer;
          gainNode.gain.value = audioManager.getVolume();
          
          source.connect(gainNode);
          gainNode.connect(audioManager.audioContext.destination);
          
          source.start(0);
          audioSource.current = source;
        }
      } catch (error) {
        console.error('Error playing qubits sound:', error);
      }
    };

    initAudio();

    // Cleanup function
    return () => {
      if (audioSource.current) {
        try {
          audioSource.current.stop();
          audioSource.current.disconnect();
        } catch (error) {
          console.error('Error stopping qubits sound:', error);
        }
      }
    };
  }, []); // Empty dependency array means this runs once on mount

  // Progress and qubit count update
  React.useEffect(() => {
    const animationDuration = 10000; // 10 seconds total
    const countingDuration = 8000;   // 8 seconds for counting
    const progressInterval = 100;     // Update every 100ms
    
    const updateInterval = setInterval(() => {
      const elapsed = Date.now() - startTime.current;
      const newProgress = Math.min(Math.floor((elapsed / animationDuration) * 100), 100);
      
      // Handle animation completion
      if (newProgress === 100 && !successPlayed.current) {
        audioManager.playSuccess();
        successPlayed.current = true;
        // Auto close after a short delay to allow success sound to play
        setTimeout(() => {
          handleClose();
        }, 1000);
      }
      
      setProgress(newProgress);

      // Calculate displayed qubits with easing
      if (elapsed < countingDuration && results) {
        const countProgress = elapsed / countingDuration;
        // Easing function for smoother counting
        const easeOutQuart = 1 - Math.pow(1 - countProgress, 4);
        // Ensure displayed qubits are whole numbers
        const targetQubits = Math.floor(results.qubits * easeOutQuart);
        setDisplayedQubits(targetQubits);
      } else if (results) {
        setDisplayedQubits(results.qubits);
      }
    }, progressInterval);

    return () => {
      clearInterval(updateInterval);
    };
  }, [results]);

  const setup = (p5, canvasParentRef) => {
    const canvas = p5.createCanvas(600, 450).parent(canvasParentRef);
    canvas.style('display', 'block !important');
    canvas.style('margin', '0 auto');
    canvas.style('backgroundColor', '#000');
    canvas.style('borderRadius', '5px');
    canvas.style('boxShadow', '0 0 20px rgba(0, 255, 0, 0.1)');
    canvas.addClass('harvest-animation');

    // Adjust particle distribution for new canvas size
    const centerX = p5.width/2;
    const centerY = p5.height/2;
    
    // Initialize particles in a wider distributed pattern
    particles.current = Array.from({ length: particleCount }, () => {
      const angle = p5.random(p5.TWO_PI);
      const radius = p5.random(50, Math.min(p5.width, p5.height) * 0.4); // Scale with canvas size
      const centerOffsetX = 0; // No horizontal offset for center
      const centerOffsetY = -20; // Slight upward shift of the center point
      return {
        x: p5.width/2 + centerOffsetX + radius * p5.cos(angle),
        y: p5.height/2 + centerOffsetY + radius * p5.sin(angle),
        size: baseSize,
        speedX: p5.random(-0.3, 0.3), // Slower movement
        speedY: p5.random(-0.3, 0.3),
        phase: p5.random(p5.TWO_PI),
        pulseOffset: p5.random(p5.TWO_PI),
        trailPoints: []
      };
    });
  };
// Define constants
const LAYOUT = {
  top: 60,
  bottom: 60,
  left: 40,
  right: 40
};

// Animation state
const getAnimationValues = (frameCount) => {
  const timeOffset = frameCount * 0.01;
  return {
    timeOffset,
    quantumFlux: Math.sin(timeOffset * 3) * 0.3 + 0.7,
    collectionPulse: Math.sin(frameCount * 0.05) * 8,
    corePulse: Math.sin(timeOffset * 4) * 5
  };
};

const draw = (p5) => {
  // Clear with fade effect
  p5.background(0, 0, 0, 20);

  const center = {
    x: p5.width/2,
    y: p5.height/2
  };

  // Get current animation values
  const anim = getAnimationValues(p5.frameCount);

  // Draw quantum field grid with dynamic intensity
  p5.stroke(0, 255, 0, 10);
  p5.strokeWeight(1);
  const gridSize = 30;
  
  for(let i = 0; i < p5.width; i += gridSize) {
    const intensity = Math.sin(i * 0.05 + anim.timeOffset) * 5 * anim.quantumFlux;
    p5.stroke(0, 255, 0, 10 + intensity);
    p5.line(i, 0, i, p5.height);
  }
  for(let i = 0; i < p5.height; i += gridSize) {
    const intensity = Math.cos(i * 0.05 + anim.timeOffset) * 5 * anim.quantumFlux;
    p5.stroke(0, 255, 0, 10 + intensity);
    p5.line(0, i, p5.width, i);
  }

  // Draw information panels with quantum effects
  p5.noStroke();
  const shimmer = Math.sin(anim.timeOffset * 2) * 3;
  
  // Only draw bottom panel with progress bar
  p5.fill(0, 255, 0, 8 + shimmer);
  p5.rect(LAYOUT.left, p5.height - LAYOUT.bottom, p5.width - LAYOUT.left * 2, LAYOUT.bottom - 10);
  
  // Progress bar in bottom panel
  const panelGlow = Math.sin(anim.timeOffset * 4) * 2;
  p5.fill(0, 255, 0, 15 + panelGlow);
  p5.rect(LAYOUT.left + 10, p5.height - LAYOUT.bottom + 15, ((progress / 100) * (p5.width - LAYOUT.left * 2 - 20)), 10);

  if (validResults) {
    // Draw quantum metrics with enhanced effects
    p5.textAlign(p5.CENTER, p5.TOP);
    p5.textFont('IBM BIOS');
    
    // Draw centered qubit count
    const safeDisplayedQubits = isNaN(displayedQubits) ? 0 : displayedQubits;
    const displayText = `${safeDisplayedQubits} QUBITS`;
    
    // Right-aligned qubit count display
    p5.textSize(36);
    p5.textAlign(p5.RIGHT, p5.TOP);
    p5.fill(0, 255, 0, 255);
    p5.text(displayText, p5.width - LAYOUT.right, 20);

    // Calculate phase values
    const currentPhase = Math.floor((progress / 100) * 4);
    const phaseOffset = currentPhase * (Math.PI / 2);
    const phaseGlow = Math.sin(anim.timeOffset * 4 + phaseOffset) * 10;
  }
    p5.background(0, 0, 0, 15);
    
    const centerX = p5.width/2;
    const centerY = p5.height/2 - 10; // Slightly above center to account for text
    // Draw quantum containment system
    p5.noFill();
    
    // Draw quantum field lines with phase-based patterns
    const currentPhase = Math.floor((progress / 100) * 4);
    const phaseOffset = currentPhase * (Math.PI / 2);
    
    for(let i = 0; i < 360; i += 12) {
      const angle = p5.radians(i);
      const fieldStrength = Math.sin(i * 0.1 + anim.timeOffset + phaseOffset) * 10;
      const innerRadius = 80 + fieldStrength;
      const outerRadius = 120 + fieldStrength;
      
      const x1 = center.x + p5.cos(angle) * innerRadius;
      const y1 = center.y + p5.sin(angle) * innerRadius;
      const x2 = center.x + p5.cos(angle) * outerRadius;
      const y2 = center.y + p5.sin(angle) * outerRadius;
      
      // Phase-based field intensity
      const phaseIntensity = Math.sin(i * 0.2 + anim.timeOffset + phaseOffset);
      const fieldIntensity = (phaseIntensity * 20 + 30) * anim.quantumFlux;
      
      p5.stroke(0, 255, 0, fieldIntensity);
      p5.line(x1, y1, x2, y2);
      
      // Add quantum interference patterns
      if (currentPhase > 1) {
        const interference = Math.sin(angle * 3 + anim.timeOffset) * 30;
        const ix = center.x + p5.cos(angle) * (100 + interference);
        const iy = center.y + p5.sin(angle) * (100 + interference);
        p5.stroke(0, 255, 0, fieldIntensity * 0.5);
        p5.line(x1, y1, ix, iy);
      }
    }
    
    // Draw quantum containment rings with phase effects
    for(let i = 4; i > 0; i--) {
      const ringPhase = Math.sin(anim.timeOffset * (2 + i * 0.5) + phaseOffset);
      const ringPulse = ringPhase * 5;
      const alpha = (100 - i * 20) * anim.quantumFlux;
      p5.stroke(0, 255, 0, alpha);
      const baseSize = 70 + i * 20;
      p5.circle(center.x, center.y, baseSize + anim.collectionPulse + ringPulse);
    }
    
    // Draw quantum core with phase-based effects
    p5.noStroke();
    const coreSize = 50 + anim.collectionPulse * 0.3;
    
    // Quantum field with phase visualization
    const phaseGlow = Math.sin(anim.timeOffset * 4 + phaseOffset) * 10;
    p5.fill(0, 255, 0, (20 + phaseGlow) * anim.quantumFlux);
    p5.circle(center.x, center.y, coreSize * 2 + anim.corePulse);
    
    // Energy field with interference
    p5.fill(0, 255, 0, (40 + phaseGlow) * anim.quantumFlux);
    p5.circle(center.x, center.y, coreSize * 1.5 + anim.corePulse);
    
    // Core with quantum state
    p5.fill(0, 255, 0, (60 + phaseGlow) * anim.quantumFlux);
    p5.circle(center.x, center.y, coreSize + anim.corePulse);
    
    // Quantum center
    p5.fill(0, 255, 0, (150 + phaseGlow) * anim.quantumFlux);
    p5.circle(center.x, center.y, coreSize * 0.5 + anim.corePulse * 0.5);
    p5.circle(centerX, centerY, 45 + anim.collectionPulse * 0.5);
    
    // Draw bottom status panel
    const bottomY = p5.height - LAYOUT.bottom + 25;
    p5.textAlign(p5.CENTER, p5.TOP);
    p5.textSize(18);
    
    // Draw harvesting status with details
    const statusPulse = Math.sin(p5.frameCount * 0.05) * 0.5 + 0.5;
    const harvestPhase = Math.floor((progress / 100) * 4); // 4 phases of harvesting
    const statusTexts = [
      "INITIALIZING QUANTUM FIELD",
      "STABILIZING PARTICLE MATRIX",
      "HARVESTING QUANTUM STATES",
      "FINALIZING COLLECTION"
    ];
    const statusText = statusTexts[Math.min(harvestPhase, 3)];
    
    // Status text glow
    for (let i = 2; i > 0; i--) {
      const alpha = (40 * statusPulse) / i;
      p5.fill(0, 255, 0, alpha);
      p5.text(statusText, p5.width/2, bottomY);
    }
    p5.fill(0, 255, 0, 180 * statusPulse);
    p5.text(statusText, p5.width/2, bottomY);

    // Update and draw particles with quantum effects
    particles.current = particles.current.filter(particle => {
      // Update trail with enhanced quantum interference
      if (p5.frameCount % 2 === 0) {
        const phaseState = Math.sin(particle.phase + anim.timeOffset) * 0.5 + 0.5;
        particle.trailPoints.unshift({
          x: particle.x,
          y: particle.y,
          phase: particle.phase,
          state: phaseState
        });
        if (particle.trailPoints.length > trailLength) {
          particle.trailPoints.pop();
        }
      }
      
      const dx = center.x - particle.x;
      const dy = center.y - particle.y;
      const dist = p5.sqrt(dx*dx + dy*dy);
      
      // Phase-based quantum field effects
      const currentPhase = Math.floor((progress / 100) * 4);
      const phaseOffset = currentPhase * (Math.PI / 2);
      const fieldStrength = Math.sin(dist * 0.02 + anim.timeOffset + phaseOffset) * 0.5 + 1.5;
      
      // Enhanced quantum attraction with phase coherence
      const attractionStrength = 0.0004 * dist * fieldStrength * anim.quantumFlux;
      const maxSpeed = 2.5 + Math.sin(anim.timeOffset * 2) * 0.5;
      
      // Apply phase-influenced movement
      const phaseInfluence = Math.sin(particle.phase + phaseOffset) * 0.2;
      particle.speedX += dx * attractionStrength + phaseInfluence;
      particle.speedY += dy * attractionStrength + phaseInfluence;
      
      // Apply quantum uncertainty to speed
      const uncertainty = Math.sin(particle.phase + p5.frameCount * 0.05) * 0.1;
      particle.speedX += uncertainty;
      particle.speedY += uncertainty;
      
      // Limit maximum speed with quantum variance
      const currentSpeed = Math.sqrt(particle.speedX * particle.speedX + particle.speedY * particle.speedY);
      if (currentSpeed > maxSpeed) {
        const scale = maxSpeed / currentSpeed;
        particle.speedX *= scale;
        particle.speedY *= scale;
      }
      
      // Add oscillation
      particle.phase += 0.02;
      const oscX = p5.sin(particle.phase) * 0.3;
      const oscY = p5.cos(particle.phase) * 0.3;
      
      // Update position with oscillation
      particle.x += particle.speedX + oscX;
      particle.y += particle.speedY + oscY;
      
      // Apply drag for smoother movement
      particle.speedX *= 0.99;
      particle.speedY *= 0.99;
      
      // Calculate pulsing size
      const pulseSize = baseSize + p5.sin(p5.frameCount * 0.05 + particle.pulseOffset) * pulseRange;
      
      // Enhanced trail effect
      if (particle.trailPoints.length > 1) {
        p5.noFill();
        p5.beginShape();
        particle.trailPoints.forEach((point, i) => {
          // Enhanced alpha fade based on distance from center
          const distFromCenter = p5.dist(point.x, point.y, centerX, centerY);
          const maxDist = Math.min(p5.width, p5.height) * 0.4;
          const distAlpha = p5.map(distFromCenter, 0, maxDist, 100, 20);
          const trailAlpha = p5.map(i, 0, particle.trailPoints.length - 1, distAlpha, 0);
          p5.stroke(0, 255, 0, trailAlpha);
          p5.vertex(point.x, point.y);
        });
        p5.endShape();
      }
      
      // Enhanced particle rendering
      p5.noStroke();
      
      // Dynamic glow based on distance from center
      const distFromCenter = p5.dist(particle.x, particle.y, centerX, centerY);
      const maxDist = Math.min(p5.width, p5.height) * 0.4;
      const glowIntensity = p5.map(distFromCenter, 0, maxDist, 50, 20);
      
      // Outer glow
      p5.fill(0, 255, 0, glowIntensity * 0.6);
      p5.circle(particle.x, particle.y, pulseSize * 2);
      
      // Inner glow
      p5.fill(0, 255, 0, glowIntensity);
      p5.circle(particle.x, particle.y, pulseSize * 1.4);
      
      // Core
      p5.fill(0, 255, 0, 200);
      p5.circle(particle.x, particle.y, pulseSize);
      
      return true;
    });

    // Draw harvesting information
    p5.textAlign(p5.CENTER);
    p5.noStroke();
    p5.textFont('IBM BIOS');
    
    // Define layout zones
    const layout = {
      top: 60,
      bottom: 60,
      left: 40,
      right: 40,
      center: {
        x: p5.width/2,
        y: p5.height/2
      }
    };

    // Draw background grid effect
    p5.stroke(0, 255, 0, 15);
    p5.strokeWeight(1);
    for(let i = 0; i < p5.width; i += 30) {
      p5.line(i, 0, i, p5.height);
    }
    for(let i = 0; i < p5.height; i += 30) {
      p5.line(0, i, p5.width, i);
    }
    
    // Draw data panel backgrounds
    p5.noStroke();
    p5.fill(0, 255, 0, 10);
    // Top panel
    p5.rect(layout.left, 10, p5.width - layout.left * 2, layout.top - 20);
    // Bottom panel
    p5.rect(layout.left, p5.height - layout.bottom, p5.width - layout.left * 2, layout.bottom - 10);

    if (validResults) {
      // Stats panel is now empty since qubit count is handled earlier

      // No additional text in top panel - keeping it clean with just the qubit count

      // Draw progress percentage above progress bar
      const progressText = `${Math.floor(progress)}%`;
      p5.textAlign(p5.RIGHT, p5.BOTTOM);
      p5.textSize(20);
      p5.fill(0, 255, 0, 200);
      p5.text(progressText, p5.width - LAYOUT.right, p5.height - LAYOUT.bottom + 10);
      
      // Draw status text at bottom
      const statusText = "HARVESTING IN PROGRESS";
      const statusPulse = Math.sin(p5.frameCount * 0.05) * 0.5 + 0.5;
      
      p5.textAlign(p5.LEFT, p5.BOTTOM);
      p5.textSize(20);
      
      // Status text position
      const statusY = p5.height - LAYOUT.bottom + 10;
      
      // Enhanced status text glow
      for (let i = 2; i > 0; i--) {
        const alpha = (40 * statusPulse) / i;
        p5.fill(0, 255, 0, alpha);
        p5.text(statusText, LAYOUT.left + 10, statusY);
      }
      
      // Draw main status text
      p5.fill(0, 255, 0, 180 * statusPulse);
      p5.text(statusText, LAYOUT.left + 10, statusY);
    }
  };

  const handleClose = () => {
    // Stop the audio if it's playing
    if (audioSource.current) {
      try {
        audioSource.current.stop();
        audioSource.current.disconnect();
      } catch (error) {
        console.error('Error stopping qubits sound:', error);
      }
    }
    onComplete();
  };

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    width: '600px', // Match new canvas width
    margin: '0 auto',
    backgroundColor: 'rgba(0, 0, 0, 0.95)', // Darker background
    border: '2px solid #00ff00',
    boxShadow: '0 0 30px rgba(0, 255, 0, 0.2)', // Enhanced glow
    borderRadius: '5px'
  };

  const contentStyle = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    height: '450px', // Match new canvas height
    padding: '10px', // Add some padding
    backgroundColor: 'rgba(0, 0, 0, 0.8)' // Slightly darker content area
  };

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none', // Allow clicking through the overlay
    zIndex: 1000
  };

  const bonusModalStyle = {
    position: 'absolute',
    left: '-220px', // Start further out
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    border: '2px solid #00ff00',
    padding: '20px',
    color: '#00ff00',
    fontFamily: 'IBM BIOS, monospace',
    fontSize: '16px', // Larger font
    width: '200px', // Wider modal
    boxShadow: '0 0 30px rgba(0, 255, 0, 0.3)',
    transition: 'left 0.4s ease-out',
    borderRadius: '0 15px 15px 0',
    pointerEvents: 'auto',
    zIndex: 1200,
    backdropFilter: 'blur(5px)' // Add blur effect
  };

  const activeBonusModalStyle = {
    ...bonusModalStyle,
    left: '20px' // Show slightly offset from edge
  };

  return (
    <>
      <div style={containerStyle}>
        <div style={titleBarStyle}>
          <span>QUBIT HARVESTING</span>
        </div>
        <div style={contentStyle}>
          <Sketch setup={setup} draw={draw} />
        </div>
      </div>

      {/* Overlay for bonus modal */}
      <div style={overlayStyle}>
        {validResults?.bonuses?.length > 0 && (
          <div style={progress > 5 ? activeBonusModalStyle : bonusModalStyle}>
          <div style={{
            marginBottom: '8px',
            borderBottom: '1px solid #00ff00',
            paddingBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ color: '#00ff99', fontSize: '12px' }}>►</span>
            <span style={{ fontSize: '14px' }}>HARVEST BONUSES</span>
          </div>
          {validResults.bonuses.map((bonus, index) => (
            <div key={index} style={{
              marginBottom: '4px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              opacity: progress > (10 + index * 5) ? 1 : 0,
              transition: 'opacity 0.3s ease-in',
              fontSize: '12px',
              padding: '2px 0'
            }}>
              <span style={{ marginRight: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bonus.type}:</span>
              <span style={{ color: '#00ff99', fontWeight: 'bold', marginLeft: 'auto' }}>{bonus.multiplier}</span>
            </div>
          ))}
          {validResults.totalMultiplier > 1 && (
            <div style={{
              marginTop: '8px',
              borderTop: '1px solid #00ff00',
              paddingTop: '4px',
              opacity: progress > 50 ? 1 : 0,
              transition: 'opacity 0.3s ease-in',
              fontSize: '13px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ marginRight: '8px' }}>Total:</span>
              <span style={{ color: '#00ff99', fontWeight: 'bold' }}>{validResults.totalMultiplier}x</span>
            </div>
          )}
          </div>
        )}
      </div>
    </>
  );
};

export default QubitHarvestingAnimation;